import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Edge,
  Connection,
  Node,
  MarkerType,
} from 'reactflow';

import {
  Box,
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Axios from 'axios';

import CustomNode from '../CustomNode/CustomNode';
import CustomEdge from '../CustomEdge/CustomEdge';
import ErrorEdge from '../ErrorEdge/ErrorEdge';
import DocumentInputComponent from '../DocumentInputComponent/DocumentInputComponent';
import DocumentTypeComponent from '../DocumentTypeComponent/DocumentTypeComponent';
import DocumentReviewComponent from '../DocumentReviewComponent/DocumentReviewComponent';
import DocumentExportComponent from '../DocumentExportComponent/DocumentExportComponent';
import DigitizeComponent from '../DigitizeComponent/DigitizeComponent';
import ConfigContext from 'src/contexts/ConfigContext';
import { useAuth } from 'src/hooks/useAuth';
import { axiosHeaders } from 'src/utils/helpers';
import {
  validateWorkflowForPublishing,
  ValidationError,
  WorkflowStatus,
  NodeType,
  availableNodeTypesConfig,
  nodeConstraints
} from '../../utils';
import { RootState } from 'src/types';
import { fetchCompanyDocTypes } from 'src/doc-types/actions';

import 'reactflow/dist/style.css';
import styles from './style';

interface ApiWorkflowComponent {
  id?: number;
  reference?: string;
  type: string;
  xCoordinate: number;
  yCoordinate: number;
  width: number;
  height: number;
  configuration: string;
}

interface ApiWorkflowEdge {
  id?: number;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
}

interface ApiWorkflowStructure {
  components: (ApiWorkflowComponent & { id: number; reference: string })[];
  edges: (ApiWorkflowEdge & { id: number })[];
}

interface SaveWorkflowRequest {
  id?: number;
  components: ApiWorkflowComponent[];
  edges: ApiWorkflowEdge[];
  active: boolean;
}

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
  error: ErrorEdge,
};

type PropsType = {
  selectedWorkflow: Record<string, any> | null;
  handleClose: () => void;
};

const ManageWorkflow = (props: PropsType) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { API } = useContext(ConfigContext);
  const { user } = useAuth();
  const { selectedWorkflow, handleClose } = props;
  const docTypes: Record<string, any>[] = useSelector((state: RootState) => state.docTypes.companyDocTypes);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [workflowName, setWorkflowName] = useState<string>('');
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const availableNodeTypes: NodeType[] = availableNodeTypesConfig.map(config => ({
    type: config.type,
    label: t(config.labelKey),
    icon: config.icon
  }));

  const [settingsDrawer, setSettingsDrawer] = useState<{
    nodeId: string;
    nodeType: string;
    label: string;
    configuration: Record<string, any>;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [originalNodesSnapshot, setOriginalNodesSnapshot] = useState<any[]>([]);
  const [originalEdgesSnapshot, setOriginalEdgesSnapshot] = useState<any[]>([]);
  const [isWorkflowActive, setIsWorkflowActive] = useState<boolean>(false);
  const [isStatusChanging, setIsStatusChanging] = useState<boolean>(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false);

  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus>('stopped');
  const [isCanvasDisabled, setIsCanvasDisabled] = useState<boolean>(true);

  const [showValidationDialog, setShowValidationDialog] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  useEffect(() => {
    if (selectedWorkflow?.id) {
      loadWorkflowStructure();
    }
  }, [selectedWorkflow?.id]);

  useEffect(() => {
    if (docTypes.length === 0) {
      dispatch(fetchCompanyDocTypes() as any);
    }
  }, [dispatch, docTypes.length]);

  // update node labels
  useEffect(() => {
    if (docTypes.length > 0 && nodes.length > 0) {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            label: getNodeLabel(node.data.nodeType, node.data.settings || {}),
          },
        }))
      );
    }
  }, [docTypes, setNodes]);

  useEffect(() => {
    const checkForChanges = () => {
      if (workflowStatus !== 'editing') {
        setHasUnsavedChanges(false);
        return;
      }

      const currentNodesJson = JSON.stringify(nodes.map(n => ({
        id: n.id,
        position: n.position,
        data: {
          nodeType: n.data.nodeType,
          settings: n.data.settings,
          reference: n.data.reference,
          backendId: n.data.backendId
        }
      })));
      const currentEdgesJson = JSON.stringify(edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        data: e.data
      })));

      const originalNodesJson = JSON.stringify(originalNodesSnapshot);
      const originalEdgesJson = JSON.stringify(originalEdgesSnapshot);

      const nodesChanged = currentNodesJson !== originalNodesJson;
      const edgesChanged = currentEdgesJson !== originalEdgesJson;

      setHasUnsavedChanges(nodesChanged || edgesChanged);
    };

    checkForChanges();
  }, [nodes, edges, originalNodesSnapshot, originalEdgesSnapshot, workflowStatus]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isCanvasDisabled || workflowStatus !== 'editing') return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        const selectedEdges = edges.filter(edge => edge.selected);
        if (selectedEdges.length > 0) {
          event.preventDefault();
          setEdges((eds) => eds.filter(edge => !edge.selected));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [edges, isCanvasDisabled, workflowStatus, setEdges]);

  const getNodeLabel = (nodeType: string, configuration: Record<string, any>) => {
    const baseLabel = availableNodeTypesConfig.find(nodeConfig => nodeConfig.type === nodeType)?.labelKey
      ? t(availableNodeTypesConfig.find(nodeConfig => nodeConfig.type === nodeType)!.labelKey)
      : nodeType;

    if (nodeType === 'document_type' && configuration?.selectedDocumentType) {
      const selectedDocType = docTypes.find(docType => docType.documentType === configuration.selectedDocumentType);
      if (selectedDocType && selectedDocType.name) {
        return `${baseLabel} - ${t(selectedDocType.name)}`;
      }
      return `${baseLabel} - ${configuration.selectedDocumentType}`;
    }

    return baseLabel;
  };

  const mapApiToReactFlow = (apiData: ApiWorkflowStructure) => {
    const componentRefToNodeId: Record<string, string> = {};

    const mappedNodes = apiData.components.map((component) => {
      let reference = component.reference;
      if (!reference) {
        const companyId = user?.companyID || 1;
        const dateTime = Date.now();
        reference = `${companyId}_${component.type}_${dateTime}`;
      }

      const nodeId = reference;
      componentRefToNodeId[reference] = nodeId;

      const parseConfiguration = (configString: string) => {
        try {
          return typeof configString === 'string' ? JSON.parse(configString) : configString || {};
        } catch (error) {
          console.warn('Failed to parse configuration:', error);
          return {};
        }
      };

      return {
        id: nodeId,
        type: 'custom',
        position: { x: component.xCoordinate, y: component.yCoordinate },
        data: {
          label: getNodeLabel(component.type, parseConfiguration(component.configuration)),
          nodeType: component.type,
          count: '0/0',
          onDeleteNode: handleDeleteNode,
          onSettingsClick: handleSettingsClick,
          settings: parseConfiguration(component.configuration),
          backendId: component.id,
          reference: reference,
        },
      };
    });

    const mappedEdges = apiData.edges.map((edge) => ({
      id: edge.id ? edge.id.toString() : `edge-${Date.now()}`,
      source: componentRefToNodeId[edge.source] || edge.source,
      target: componentRefToNodeId[edge.target] || edge.target,
      type: 'custom',
      data: { backendId: edge.id },
      markerEnd: {
        type: MarkerType.Arrow,
        width: 15,
        height: 15,
        color: '#A3A3A3',
      }
    }));

    return { mappedNodes, mappedEdges };
  };

  const mapReactFlowToApi = (isUpdate: boolean = false, activeStatus?: boolean): SaveWorkflowRequest => {
    const nodeIdToReference: Record<string, string> = {};

    const components = nodes.map((node) => {
      const backendId = node.data.backendId;
      const existingReference = node.data.reference;
      const isExisting = !!backendId;

      let reference = existingReference;
      if (!reference) {
        const companyId = user?.companyID || 1;
        const dateTime = Date.now();
        reference = `${companyId}_${node.data.nodeType}_${dateTime}`;
      }

      nodeIdToReference[node.id] = reference;

      const component: ApiWorkflowComponent = {
        type: node.data.nodeType,
        xCoordinate: node.position.x,
        yCoordinate: node.position.y,
        width: 200,
        height: 80,
        configuration: JSON.stringify(node.data.settings || {}),
        reference,
      };

      if (isExisting) {
        component.id = backendId;
      }

      return component;
    });

    const validEdges = edges.filter((edge) => {
      const sourceExists = nodeIdToReference[edge.source] !== undefined;
      const targetExists = nodeIdToReference[edge.target] !== undefined;

      return sourceExists && targetExists;
    });

    const apiEdges = validEdges.map((edge) => {
      const backendId = edge.data?.backendId;
      const isExisting = !!backendId;

      const apiEdge: ApiWorkflowEdge = {
        source: nodeIdToReference[edge.source],
        target: nodeIdToReference[edge.target],
        sourceHandle: '',
        targetHandle: '',
      };

      if (isExisting) {
        apiEdge.id = backendId;
      }

      return apiEdge;
    });

    const result: SaveWorkflowRequest = {
      components,
      edges: apiEdges,
      active: activeStatus !== undefined ? activeStatus : isWorkflowActive,
    };

    if (isUpdate && (selectedWorkflow?.id || selectedWorkflow?.workflow)) {
      result.id = selectedWorkflow?.id || selectedWorkflow?.workflow || 0;
    }

    return result;
  };

  const loadWorkflowStructure = async () => {
    if (!selectedWorkflow?.id) return;

    setIsLoading(true);
    try {
      const workflowId = selectedWorkflow.workflow || selectedWorkflow.id;
      const response = await Axios.get(
        `${API.workflowStructure}/${workflowId}/structure`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data && response.status === 200) {
        const workflowStructure = response.data.data;
        if (workflowStructure && workflowStructure.components && workflowStructure.edges) {
          const { mappedNodes, mappedEdges } = mapApiToReactFlow(workflowStructure);
          setNodes(mappedNodes);
          setEdges(mappedEdges);

          setWorkflowName(workflowStructure.name || t('WORKFLOW_CANVAS_DEFAULT_NAME'));

          const nodesSnapshot = mappedNodes.map(n => ({
            id: n.id,
            position: n.position,
            data: {
              nodeType: n.data.nodeType,
              settings: n.data.settings,
              reference: n.data.reference,
              backendId: n.data.backendId
            }
          }));
          const edgesSnapshot = mappedEdges.map(e => ({
            id: e.id,
            source: e.source,
            target: e.target,
            data: e.data
          }));

          setOriginalNodesSnapshot(nodesSnapshot);
          setOriginalEdgesSnapshot(edgesSnapshot);
          setHasUnsavedChanges(false);

          const activeStatus = workflowStructure.active !== undefined
            ? workflowStructure.active
            : selectedWorkflow?.active !== undefined
              ? selectedWorkflow.active
              : false;
          setIsWorkflowActive(activeStatus);

          if (activeStatus) {
            setWorkflowStatus('running');
            setIsCanvasDisabled(true);
          } else {
            if (mappedNodes.length === 0) {
              setWorkflowStatus('editing');
              setIsCanvasDisabled(false);
            } else {
              setWorkflowStatus('stopped');
              setIsCanvasDisabled(true);
            }
          }
        } else {
          setNodes([]);
          setEdges([]);
          setOriginalNodesSnapshot([]);
          setOriginalEdgesSnapshot([]);
          setHasUnsavedChanges(false);

          const activeStatus = selectedWorkflow?.active !== undefined
            ? selectedWorkflow.active
            : false;
          setIsWorkflowActive(activeStatus);

          if (activeStatus) {
            setWorkflowStatus('running');
            setIsCanvasDisabled(true);
          } else {
            setWorkflowStatus('editing');
            setIsCanvasDisabled(false);
          }
        }
      }
    } catch (error) {
      console.error('Error loading workflow structure:', error);
      setErrorMessage(t('WORKFLOW_ERROR_LOAD_FAILED'));
    } finally {
      setIsLoading(false);
    }
  };

  const saveWorkflowStructure = async (): Promise<boolean> => {
    if (!selectedWorkflow?.id) {
      setErrorMessage(t('WORKFLOW_ERROR_NO_WORKFLOW_SELECTED'));
      return false;
    }

    setIsSaving(true);
    try {
      const workflowData = mapReactFlowToApi(true);
      const workflowId = selectedWorkflow.workflow || selectedWorkflow.id;

      const response = await Axios.put(
        `${API.workflowStructure}/${workflowId}/structure`,
        workflowData,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.status === 200 || response.status === 204) {
        setErrorMessage('');
        setSuccessMessage(t('WORKFLOW_SAVE_SUCCESS'));
        setTimeout(() => setSuccessMessage(''), 3000);

        // deselect nodes
        setNodes((nds) =>
          nds.map((n) => ({
            ...n,
            selected: false
          }))
        );

        // deselect edges
        setEdges((eds) =>
          eds.map((e) => ({
            ...e,
            selected: false
          }))
        );

        const nodesSnapshot = nodes.map(n => ({
          id: n.id,
          position: n.position,
          data: {
            nodeType: n.data.nodeType,
            settings: n.data.settings,
            reference: n.data.reference,
            backendId: n.data.backendId
          }
        }));
        const edgesSnapshot = edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          data: e.data
        }));

        setOriginalNodesSnapshot(nodesSnapshot);
        setOriginalEdgesSnapshot(edgesSnapshot);
        setHasUnsavedChanges(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving workflow structure:', error);
      setErrorMessage(t('WORKFLOW_ERROR_SAVE_FAILED'));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleStopWorkflow = async () => {
    if (!selectedWorkflow?.id) {
      setErrorMessage(t('WORKFLOW_ERROR_NO_WORKFLOW_SELECTED'));
      return;
    }

    setIsStatusChanging(true);
    const originalStatus = workflowStatus;
    const originalCanvasDisabled = isCanvasDisabled;
    const originalWorkflowActive = isWorkflowActive;

    try {
      const workflowId = selectedWorkflow.workflow || selectedWorkflow.id;

      const response = await Axios.put(
        `${API.workflowStructure}/${workflowId}/status`,
        { active: false },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.status === 200 || response.status === 204) {
        setIsWorkflowActive(false);
        setWorkflowStatus('stopped');
        setIsCanvasDisabled(true);
        setErrorMessage('');
        setSuccessMessage(t('WORKFLOW_STOP_SUCCESS'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setWorkflowStatus(originalStatus);
        setIsCanvasDisabled(originalCanvasDisabled);
        setIsWorkflowActive(originalWorkflowActive);
        setErrorMessage(t('WORKFLOW_ERROR_STOP_FAILED'));
      }
    } catch (error) {
      console.error('Error stopping workflow:', error);
      setErrorMessage(t('WORKFLOW_ERROR_STOP_FAILED'));

      setWorkflowStatus(originalStatus);
      setIsCanvasDisabled(originalCanvasDisabled);
      setIsWorkflowActive(originalWorkflowActive);
    } finally {
      setIsStatusChanging(false);
    }
  };

  const handleEditWorkflow = () => {
    setWorkflowStatus('editing');
    setIsCanvasDisabled(false);
    setHasUnsavedChanges(false);
  };

  const handleSaveWorkflow = async () => {
    const saveSuccess = await saveWorkflowStructure();
    if (saveSuccess) {
      setWorkflowStatus('stopped');
      setIsCanvasDisabled(true);
    }
  };

  const handlePublishWorkflow = async () => {
    if (!selectedWorkflow?.id) {
      setErrorMessage(t('WORKFLOW_ERROR_NO_WORKFLOW_SELECTED'));
      return;
    }

    const validationResult = validateWorkflowForPublishing(nodes);
    if (!validationResult.isValid) {
      setValidationErrors(validationResult.errors);
      setShowValidationDialog(true);
      return;
    }

    setIsStatusChanging(true);
    const originalStatus = workflowStatus;
    const originalCanvasDisabled = isCanvasDisabled;

    try {
      const workflowId = selectedWorkflow.workflow || selectedWorkflow.id;

      const response = await Axios.put(
        `${API.workflowStructure}/${workflowId}/status`,
        { active: true },
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.status === 200 || response.status === 204) {
        setIsWorkflowActive(true);
        setWorkflowStatus('published');
        setIsCanvasDisabled(true);
        setErrorMessage('');
        setSuccessMessage(t('WORKFLOW_PUBLISH_SUCCESS'));
        setTimeout(() => setSuccessMessage(''), 3000);

        setTimeout(() => {
          setWorkflowStatus('running');
        }, 2000);
      } else {
        setWorkflowStatus(originalStatus);
        setIsCanvasDisabled(originalCanvasDisabled);
        setErrorMessage(t('WORKFLOW_ERROR_PUBLISH_FAILED'));
      }
    } catch (error) {
      console.error('Error publishing workflow:', error);
      setErrorMessage(t('WORKFLOW_ERROR_PUBLISH_FAILED'));
      setWorkflowStatus(originalStatus);
      setIsCanvasDisabled(originalCanvasDisabled);
    } finally {
      setIsStatusChanging(false);
    }
  };

  const onInit = useCallback(() => { }, []);

  const onDragStart = (event: React.DragEvent, nodeType: string, label: string) => {
    event.dataTransfer.setData('application/reactflow/type', 'custom');
    event.dataTransfer.setData('application/reactflow/nodeType', nodeType);
    event.dataTransfer.setData('application/reactflow/label', label);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const canAddNode = (nodeType: string): boolean => {
    const constraint = nodeConstraints[nodeType];
    if (!constraint) return false;

    if (constraint.maxCount === -1) return true;

    const existingCount = nodes.filter(node => node.data.nodeType === nodeType).length;
    return existingCount < constraint.maxCount;
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      const nodeType = event.dataTransfer.getData('application/reactflow/nodeType');
      const label = event.dataTransfer.getData('application/reactflow/label');

      if (!nodeType || !label) return;

      if (!canAddNode(nodeType)) {
        const constraint = nodeConstraints[nodeType];
        setErrorMessage(t('WORKFLOW_ERROR_MAX_NODES', { count: constraint.maxCount, label }));
        return;
      }

      const position = {
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      };

      const newNodeId = `${nodeType}-${Date.now()}`;
      const companyId = user?.companyID || 1;
      const dateTime = Date.now();
      const reference = `${companyId}_${nodeType}_${dateTime}`;

      const newNode = {
        id: newNodeId,
        type: 'custom',
        position,
        data: {
          label: getNodeLabel(nodeType, {}),
          nodeType,
          count: '0/0',
          onDeleteNode: handleDeleteNode,
          onSettingsClick: handleSettingsClick,
          reference,
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [setNodes, nodes]
  );

  const isValidConnection = (source: Node | undefined | null, target: Node | undefined | null): boolean => {
    if (!source || !target) return false;

    const sourceType = source?.data?.nodeType;
    const targetType = target?.data?.nodeType;

    const sourceConstraint = nodeConstraints[sourceType];
    const targetConstraint = nodeConstraints[targetType];

    if (!sourceConstraint || !targetConstraint) return false;

    const canSourceConnectToTarget = sourceConstraint.canConnectTo.includes(targetType);
    const canTargetReceiveFromSource = targetConstraint.canConnectFrom.includes(sourceType);

    return canSourceConnectToTarget && canTargetReceiveFromSource;
  };

  const getConnectionErrorMessage = (sourceNode: Node | null, targetNode: Node | null): string => {
    if (!sourceNode || !targetNode) return t('WORKFLOW_INVALID_CONNECTION');

    const sourceType = sourceNode.data?.nodeType;
    const targetType = targetNode.data?.nodeType;
    const sourceLabel = sourceNode.data?.label;
    const targetLabel = targetNode.data?.label;

    if (sourceType === targetType) {
      return t('WORKFLOW_ERROR_SAME_NODE_TYPE', { sourceLabel, targetLabel });
    }

    if (sourceType === 'document_input') {
      if (!['digitize', 'document_type', 'document_review', 'document_export'].includes(targetType)) {
        return t('WORKFLOW_ERROR_DOCUMENT_INPUT_CONNECTION');
      }
    }

    if (sourceType === 'digitize') {
      if (!['document_type', 'document_review', 'document_export'].includes(targetType)) {
        return t('WORKFLOW_ERROR_DIGITIZE_CONNECTION');
      }
    }

    if (sourceType === 'document_type') {
      if (!['document_review', 'document_export'].includes(targetType)) {
        return t('WORKFLOW_ERROR_DOCUMENT_TYPE_CONNECTION');
      }
    }

    if (sourceType === 'document_review') {
      if (targetType !== 'document_export') {
        return t('WORKFLOW_ERROR_REVIEW_CONNECTION');
      }
    }

    if (sourceType === 'document_export') {
      if (targetType !== 'document_review') {
        return t('WORKFLOW_ERROR_EXPORT_CONNECTION');
      }
    }

    if (targetType === 'document_input') {
      return t('WORKFLOW_ERROR_CANNOT_CONNECT_TO_INPUT');
    }

    if (targetType === 'digitize' && sourceType !== 'document_input') {
      return t('WORKFLOW_ERROR_DIGITIZE_INPUT_ONLY');
    }

    if (targetType === 'document_type' && !['document_input', 'digitize'].includes(sourceType)) {
      return t('WORKFLOW_ERROR_DOCUMENT_TYPE_INPUT');
    }

    if (targetType === 'document_review' && !['document_input', 'digitize', 'document_type'].includes(sourceType)) {
      return t('WORKFLOW_ERROR_REVIEW_INPUT');
    }

    return t('WORKFLOW_ERROR_CANNOT_CONNECT', { sourceLabel, targetLabel });
  };

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const sourceNode = nodes.find(node => node.id === params.source);
      const targetNode = nodes.find(node => node.id === params.target);

      if (!isValidConnection(sourceNode, targetNode)) {
        const errorMessage = getConnectionErrorMessage(sourceNode || null, targetNode || null);
        setErrorMessage(errorMessage);
        return;
      }

      setEdges((eds) =>
        addEdge({
          ...params,
          type: 'custom',
          markerEnd: {
            type: MarkerType.Arrow,
            width: 15,
            height: 15,
            color: '#A3A3A3',
          }
        }, eds)
      );
    },
    [setEdges, nodes]
  );

  const handleDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const handleSettingsClick = (nodeId: string, nodeType: string, label: string) => {
    const node = nodes.find(n => n.id === nodeId);
    setSettingsDrawer({
      nodeId,
      nodeType,
      label,
      configuration: node?.data?.settings || {}
    });
  };

  const handleConfigurationSave = async (nodeId: string, configuration: Record<string, any>) => {
    if (!selectedWorkflow?.id) {
      setErrorMessage(t('WORKFLOW_ERROR_NO_WORKFLOW_SELECTED'));
      return;
    }

    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      setErrorMessage(t('WORKFLOW_ERROR_NODE_NOT_FOUND'));
      return;
    }

    const workflowId = selectedWorkflow.workflow || selectedWorkflow.id;
    const componentType = node.data.nodeType;
    const apiComponentType = componentType;
    const existingComponentId = node.data.backendId;

    try {
      setIsSaving(true);

      const requestBody: { id?: number; configuration: Record<string, any> } = {
        configuration,
      };

      if (existingComponentId) {
        requestBody.id = existingComponentId;
      }

      const response = await Axios.post(
        `${API.workflows}/${workflowId}/component/${apiComponentType}`,
        requestBody,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data && response.data.success) {
        const responseData = response.data.data;
        const componentId = responseData.id;

        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? {
                ...n,
                data: {
                  ...n.data,
                  label: getNodeLabel(n.data.nodeType, configuration),
                  settings: configuration,
                  backendId: componentId
                }
              }
              : n
          )
        );

        setSuccessMessage(t('WORKFLOW_COMPONENT_SAVE_SUCCESS'));
        setTimeout(() => setSuccessMessage(''), 3000);
        setSettingsDrawer(null);
      } else {
        setErrorMessage(t('WORKFLOW_COMPONENT_SAVE_FAILED'));
      }
    } catch (error: any) {
      console.error('Error saving component configuration:', error);
      const errorMessage = error?.response?.data?.message || t('WORKFLOW_COMPONENT_SAVE_FAILED');
      setErrorMessage(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseWithWarning = () => {
    if (hasUnsavedChanges && workflowStatus === 'editing') {
      setShowUnsavedChangesDialog(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    handleClose();
  };

  const handleCancelLeave = () => {
    setShowUnsavedChangesDialog(false);
  };

  const handleCloseDrawer = () => setSettingsDrawer(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileLoad = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workflowData = JSON.parse(e.target?.result as string);
        if (workflowData.nodes && workflowData.edges) {
          setNodes(workflowData.nodes);
          setEdges(workflowData.edges);
        }
      } catch (error) {
        console.error('Error loading workflow:', error);
        setErrorMessage(t('WORKFLOW_ERROR_IMPORT_FAILED'));
      }
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [setNodes, setEdges]);

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box sx={styles.backButton} onClick={handleCloseWithWarning}>
          <KeyboardBackspaceIcon fontSize="small" />
          <Typography variant="body2">
            {t('WORKFLOW_TITLE_FORMAT', {
              workflowName: workflowName || '',
              projectName: selectedWorkflow?.name || ''
            })}
          </Typography>
        </Box>

        <Box sx={styles.status}>
          <Typography sx={styles.statusLabel}>{t('WORKFLOW_STATUS_LABEL')}:</Typography>
          <Box sx={
            workflowStatus === 'running' ? styles.statusRunning :
              workflowStatus === 'editing' ? styles.statusEditing :
                workflowStatus === 'published' ? styles.statusPublished :
                  styles.statusStopped
          }>
            {workflowStatus === 'running' && t('WORKFLOW_STATUS_RUNNING')}
            {workflowStatus === 'stopped' && t('WORKFLOW_STATUS_STOPPED')}
            {workflowStatus === 'editing' && t('WORKFLOW_STATUS_EDITING')}
            {workflowStatus === 'published' && t('WORKFLOW_STATUS_PUBLISHED')}
          </Box>

          {workflowStatus === 'running' && (
            <Button
              sx={styles.stopButton}
              onClick={handleStopWorkflow}
              disabled={isStatusChanging}
            >
              {isStatusChanging ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                t('WORKFLOW_BUTTON_STOP')
              )}
            </Button>
          )}

          {workflowStatus === 'stopped' && (
            <>
              <Button
                variant="outlined"
                onClick={handleEditWorkflow}
                sx={styles.editButton}
              >
                {t('WORKFLOW_BUTTON_EDIT')}
              </Button>
              {nodes.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handlePublishWorkflow}
                  disabled={isStatusChanging}
                  sx={styles.publishButton}
                >
                  {isStatusChanging ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    t('WORKFLOW_BUTTON_PUBLISH')
                  )}
                </Button>
              )}
            </>
          )}

          {workflowStatus === 'editing' && (
            <Tooltip
              title={nodes.length === 0 ? t('WORKFLOW_SAVE_DISABLED_TOOLTIP') : ''}
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSaveWorkflow}
                  disabled={isSaving || nodes.length === 0}
                  sx={styles.saveButton}
                >
                  {isSaving ? <CircularProgress size={20} color="inherit" /> : t('WORKFLOW_BUTTON_SAVE')}
                </Button>
              </span>
            </Tooltip>
          )}

          {workflowStatus === 'published' && (
            <Box sx={styles.publishingContainer}>
              <CircularProgress size={16} sx={styles.publishingSpinner} />
              <Typography sx={styles.publishingText}>
                {t('WORKFLOW_PUBLISHING')}
              </Typography>
            </Box>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileLoad}
            accept=".json"
            style={{ display: 'none' }}
          />
        </Box>
      </Box>

      <Box sx={styles.mainContainer}>
        <Box sx={styles.sidePanel}>
          {availableNodeTypes.map((nodeType) => (
            <Box
              key={nodeType.type}
              sx={{
                ...styles.nodeType,
                ...(isLoading || isCanvasDisabled ? styles.nodeTypeDisabled : {}),
              }}
              draggable={!isLoading && !isCanvasDisabled}
              onDragStart={(isLoading || isCanvasDisabled) ? undefined : (event) => onDragStart(event, nodeType.type, nodeType.label)}
            >
              {nodeType.icon}
              <Typography sx={styles.nodeTypeText}>{nodeType.label}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={styles.canvas} ref={reactFlowWrapper}
          onDragOver={(isLoading || isCanvasDisabled) ? undefined : onDragOver}
          onDrop={(isLoading || isCanvasDisabled) ? undefined : onDrop}
        >
          {isLoading ? (
            <Box sx={styles.loadingContainer}>
              <CircularProgress size={40} sx={styles.loadingSpinner} />
              <Typography variant="body1" sx={styles.loadingText}>
                {t('WORKFLOW_LOADING_STRUCTURE')}
              </Typography>
            </Box>
          ) : (
            <ReactFlow
              nodes={nodes.map((node) => ({
                ...node,
                data: {
                  ...node.data,
                  onDeleteNode: isCanvasDisabled ? undefined : handleDeleteNode,
                  onSettingsClick: isCanvasDisabled ? undefined : handleSettingsClick,
                  isCanvasDisabled: isCanvasDisabled,
                  workflowStatus: workflowStatus,
                },
              }))}
              edges={edges.map((edge) => ({
                ...edge,
                data: {
                  ...edge.data,
                  isCanvasDisabled: isCanvasDisabled,
                },
              }))}
              onNodesChange={isCanvasDisabled ? undefined : onNodesChange}
              onEdgesChange={isCanvasDisabled ? undefined : onEdgesChange}
              onConnect={isCanvasDisabled ? undefined : onConnect}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView={false}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              onInit={onInit}
              nodesDraggable={!isCanvasDisabled}
              nodesConnectable={!isCanvasDisabled}
              elementsSelectable={true}
              selectNodesOnDrag={false}
              multiSelectionKeyCode={['Meta', 'Ctrl']}
              deleteKeyCode={null}
              onPaneClick={() => {
                if (!isCanvasDisabled) {
                  setNodes((nds) => nds.map(node => ({ ...node, selected: false })));
                  setEdges((eds) => eds.map(edge => ({ ...edge, selected: false })));
                }
              }}
              proOptions={{ hideAttribution: true }}
            >
              <Background variant={BackgroundVariant.Dots} gap={25} size={1.5} color="#9ca3af" />
              <Controls position="bottom-right" />
              {nodes.length === 0 && (
                <Box sx={styles.emptyCanvasMessage}>
                  <Typography variant="h6" sx={styles.emptyCanvasText}>
                    {t('WORKFLOW_EMPTY_CANVAS_MESSAGE')}
                  </Typography>
                </Box>
              )}
            </ReactFlow>
          )}
        </Box>
      </Box>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={[styles.alertContainer, styles.errorAlert]}
      >
        <Alert
          severity="error"
          onClose={() => setErrorMessage('')}
          icon={<CancelIcon />}
          sx={{
            '& .MuiAlert-action': {
              display: 'none'
            }
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={[styles.alertContainer, styles.successAlert]}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessMessage('')}
          icon={<CheckCircleIcon />}
          sx={{
            '& .MuiAlert-action': {
              display: 'none'
            }
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      {settingsDrawer?.nodeType === 'document_input' ? (
        <DocumentInputComponent
          open={!!settingsDrawer}
          onClose={handleCloseDrawer}
          nodeType={settingsDrawer?.nodeType || null}
          nodeId={settingsDrawer?.nodeId || null}
          label={settingsDrawer?.label || null}
          configuration={settingsDrawer?.configuration || {}}
          isSaving={isSaving}
          onConfigurationSave={handleConfigurationSave}
        />
      ) : settingsDrawer?.nodeType === 'document_type' ? (
        <DocumentTypeComponent
          open={!!settingsDrawer}
          onClose={handleCloseDrawer}
          nodeType={settingsDrawer?.nodeType || null}
          nodeId={settingsDrawer?.nodeId || null}
          label={settingsDrawer?.label || null}
          configuration={settingsDrawer?.configuration || {}}
          isSaving={isSaving}
          onConfigurationSave={handleConfigurationSave}
        />
      ) : settingsDrawer?.nodeType === 'document_review' ? (
        <DocumentReviewComponent
          open={!!settingsDrawer}
          onClose={handleCloseDrawer}
          nodeType={settingsDrawer?.nodeType || null}
          nodeId={settingsDrawer?.nodeId || null}
          label={settingsDrawer?.label || null}
          configuration={settingsDrawer?.configuration || {}}
          isSaving={isSaving}
          onConfigurationSave={handleConfigurationSave}
        />
      ) : settingsDrawer?.nodeType === 'document_export' ? (
        <DocumentExportComponent
          open={!!settingsDrawer}
          onClose={handleCloseDrawer}
          nodeType={settingsDrawer?.nodeType || null}
          nodeId={settingsDrawer?.nodeId || null}
          label={settingsDrawer?.label || null}
          configuration={settingsDrawer?.configuration || {}}
          isSaving={isSaving}
          onConfigurationSave={handleConfigurationSave}
        />
      ) : settingsDrawer?.nodeType === 'digitize' ? (
        <DigitizeComponent
          open={!!settingsDrawer}
          onClose={handleCloseDrawer}
          nodeType={settingsDrawer?.nodeType || null}
          nodeId={settingsDrawer?.nodeId || null}
          label={settingsDrawer?.label || null}
          configuration={settingsDrawer?.configuration || {}}
          isSaving={isSaving}
          onConfigurationSave={handleConfigurationSave}
        />
      ) : null}

      <Dialog
        open={showUnsavedChangesDialog}
        onClose={handleCancelLeave}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('WORKFLOW_UNSAVED_CHANGES_TITLE')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('WORKFLOW_UNSAVED_CHANGES_MESSAGE')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelLeave}
            variant="outlined"
            sx={styles.dialogButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLeave}
            variant="contained"
            color="error"
            sx={styles.dialogButton}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('WORKFLOW_VALIDATION_ERRORS_TITLE')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('WORKFLOW_VALIDATION_ERRORS_MESSAGE')}
          </Typography>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {validationErrors.map((error, index) => (
              <Alert
                key={index}
                severity="error"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">
                  <strong>{error.nodeLabel || `Node ${error.nodeId}`}:</strong> {
                    error.message === 'WORKFLOW_VALIDATION_REVIEW_LOAD_PERCENTAGE'
                      ? t(error.message, { current: error.context?.totalLoadPercentage })
                      : t(error.message)
                  }
                </Typography>
              </Alert>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowValidationDialog(false)}
            variant="contained"
            sx={styles.dialogButton}
          >
            {t('WORKFLOW_BUTTON_OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageWorkflow;
